import { Routes } from 'config/routes';

import { BRAND_COLORS } from '../../../constants/styles';
import { ITabLinkConfig } from '../../../contexts/TabLinkContext';
import { STATUS_COLORS } from '../constants';

export const DONOR_LIST_TAB_CONFIGS: Record<string, ITabLinkConfig> = {
  activeDonors: {
    id: 'active-donors',
    title: 'Active Donors List',
    description:
      'Complete list of all currently active donors in the program. Active donors are those with qualifying donation statuses based on configured settings.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: STATUS_COLORS.good.fill },
  },
  pendingDonors: {
    id: 'pending-donors',
    title: 'Pending Donors List',
    description:
      'Complete list of all donors with a pending status. Pending donors are awaiting approval or initial screening.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: STATUS_COLORS.ok.fill },
  },
  approvedDonors: {
    id: 'approved-donors',
    title: 'Approved Donors List',
    description:
      'Complete list of all donors with an approved status. These donors have been approved and are cleared for donation.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: STATUS_COLORS.ok.fill },
  },
  retentionHistory: {
    id: 'retention-history',
    title: 'Program Retention History',
    description:
      'Historical breakdown of donor retention scores over time. Track how well the program retains donors across different periods.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: STATUS_COLORS.ok.fill },
  },
  dropoutHistory: {
    id: 'dropout-history',
    title: 'Quarterly Dropout History',
    description:
      'Historical trends of donor dropout rates by quarter. Identify patterns and seasonal variations in donor attrition.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: STATUS_COLORS.good.fill },
  },
  ratioHistory: {
    id: 'ratio-history',
    title: 'First-Time vs Repeat Ratio History',
    description:
      'Historical breakdown of the ratio between first-time and repeat donors. Track the balance of new donor acquisition vs. donor retention.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: STATUS_COLORS.good.fill },
  },
  churnDetailsMonthly: {
    id: 'churn-details-monthly',
    title: 'Monthly Churn Details',
    description:
      'Detailed breakdown of donor churn for the current month. View which donors have churned and potential reasons for attrition.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: STATUS_COLORS.good.fill },
  },
  churnDetailsQuarterly: {
    id: 'churn-details-quarterly',
    title: 'Quarterly Churn Details',
    description:
      'Detailed breakdown of donor churn for the current quarter. Analyze quarterly churn patterns and identify at-risk donor segments.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: STATUS_COLORS.ok.fill },
  },
  showRateTrends: {
    id: 'show-rate-trends',
    title: 'Show Rate Trends',
    description:
      'Historical trends of donor show rates over time. Track appointment attendance patterns and identify opportunities to improve show rates.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: STATUS_COLORS.good.fill },
  },
  singleDonors: {
    id: 'single-donors',
    title: 'Single Donors',
    description:
      'Donors who have donated a single unit. These donors have the potential to be converted to double-unit donors with proper eligibility screening.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: STATUS_COLORS.ok.fill },
  },
  doubleDonors: {
    id: 'double-donors',
    title: 'Double Donors',
    description:
      'Donors who have qualified and donated double units. These high-value donors significantly contribute to blood product inventory.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: STATUS_COLORS.good.fill },
  },
  registrationsWeek: {
    id: 'registrations-week',
    title: 'New Registrations - This Week',
    description:
      'List of donors who registered in the last 7 days. Track weekly donor acquisition and identify recruitment trends.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: '#2f3a45' },
  },
  registrationsMonth: {
    id: 'registrations-month',
    title: 'New Registrations - This Month',
    description:
      'List of donors who registered in the last 30 days. Analyze monthly donor acquisition performance.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: BRAND_COLORS.secondary.steelBlue },
  },
  registrationsQuarter: {
    id: 'registrations-quarter',
    title: 'New Registrations - This Quarter',
    description:
      'List of donors who registered in the last 90 days. Review quarterly donor acquisition against targets.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: BRAND_COLORS.primary.deepNavy },
  },
  registrationsYear: {
    id: 'registrations-year',
    title: 'New Registrations - This Year',
    description:
      'List of donors who registered in the last 365 days. Comprehensive view of annual donor acquisition.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: BRAND_COLORS.primary.royalBlue },
  },
  newDonorsYesterday: {
    id: 'new-donors-yesterday',
    title: 'New Donors - Yesterday',
    description:
      'Donors who completed their first donation yesterday. These new donors are critical to follow up with to ensure a positive experience and encourage return visits.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: '#f31a25' },
  },
  prbcDonations: {
    id: 'prbc-donations',
    title: 'pRBC Donations - Donors',
    description:
      'Donors linked to pRBC donations within the selected period and filters. This shows donor data associated with successful blood donations that contributed to the pRBC count.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: BRAND_COLORS.secondary.steelBlue },
  },
  prbcDonationsQuarterly: {
    id: 'prbc-donations-quarterly',
    title: 'pRBC Donations - Donors (Quarterly)',
    description:
      'Donors linked to pRBC donations for the current quarter. This shows donor data associated with successful blood donations that contributed to the quarterly pRBC count.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: '#4f6d7a' },
  },
  ffpDonations: {
    id: 'ffp-donations',
    title: 'FFP Units',
    description:
      'Individual FFP product units within the selected period and filters.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: '#7b2d8e' },
  },
  recruiterDonors: {
    id: 'recruiter-donors',
    title: 'Recruited Donors',
    description:
      "Donors recruited by this recruiter during the selected period. Attribution is based on the recruiter's assigned region.",
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: BRAND_COLORS.primary.deepNavy },
  },
  recruiterLocations: {
    id: 'recruiter-locations',
    title: 'Recruiter Assigned Locations',
    description:
      'Locations assigned to this recruiter, with active donor count and latest donation date.',
    targetRoute: Routes.DashboardsRecruiterDonorDetails,
    metadata: { iconColor: BRAND_COLORS.primary.deepNavy },
  },
};
